import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Divider,
  Flex,
  HStack,
  Input,
  Radio,
  RadioGroup,
  Select,
  SimpleGrid,
  Text,
  useToast,
  VStack,
} from "@chakra-ui/react";
import IssbContainer from "../IssbContainer";
import { useNavigate, useSearchParams } from "react-router-dom";
import { routes } from "../../../config";
import { useGetActiveAudit, useGetOrganizationEntities, useStartAudit } from "../../../hooks/query";
import { hideAppSpinner, showAppSpinner } from "../../../context/Store/slices/appSpinnerSlice";
import { constructQueryParamsString, showErrorToast } from "../../../utils";
import { useDispatch } from "react-redux";
import { formatDateForAPI, getCurrentDate } from "../ISSB-helpers";
import { useGetUsers } from "../../../hooks/query/user-administration";
import { MultiSelect } from "../../../components";
import LocalSpinner from "../../../components/LocalSpinner";
import useAirUserRoles from "../../../utils/AuthUtils";
import { sortByLabel } from "../../../utils/userSort";

const IssbStartAudit = () => {
  const [auditType, setAuditType] = useState("QUESTION");
  const [lockAnswers, setLockAnswers] = useState("No");
  const [autoClose, setAutoClose] = useState("No");
  const currentDate = getCurrentDate();
  const [startDate, setStartDate] = useState(currentDate);
  const [endDate, setEndDate] = useState({ day: "", month: "", year: "" });
  const [selectedAuditors, setSelectedAuditors] = useState([]);
  const [userList, setUserList] = useState([]);

  const [searchParams] = useSearchParams();
  const stageId = parseInt(searchParams.get("stageId"));
  const stageName = searchParams.get("stageName");

  const navigate = useNavigate();
  const toast = useToast();
  const dispatch = useDispatch();

  const { isController, getAirRoles } = useAirUserRoles();

  // only controller can start audit
  useEffect(() => {
    if (!isController()) {
      const controllers = getAirRoles()?.controllers;
      showErrorToast(
        toast,
        "Sorry: Only Round Controllers " + (controllers || "") + " or Organisation Admins can start audits"
      );
      navigate(routes.issbRequirements);
    }
  }, [isController, navigate, getAirRoles, toast]);

  const getRequestConfig = () => {
    return {
      onMutate: () => dispatch(showAppSpinner()),
      onSuccess: () => {
        navigate(`${routes.issbRequirements}?rtab=3`);
      },
      onError: (err) => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message),
      onSettled: () => dispatch(hideAppSpinner()),
    };
  };
  const { mutate: saveAudit } = useStartAudit(getRequestConfig());

  const handleDateChange = (type, field, value) => {
    if (type === "start") {
      setStartDate((prev) => ({ ...prev, [field]: value }));
    } else {
      setEndDate((prev) => ({ ...prev, [field]: value }));
    }
  };

  const getUsersQueryParamsString = () => {
    return constructQueryParamsString({
      page: 0,
      size: 1000,
    });
  };

  useGetUsers(getUsersQueryParamsString(), {
    onSuccess: (data) => {
      const users = sortByLabel(
        data.page?.content?.map((user) => {
          return {
            value: user?.email,
            label: user?.email,
          };
        }),
        (option) => option.label
      );
      setUserList(users);
    },
    onError: (err) => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message),
    cacheTime: 0,
  });

  const handleStartAudit = () => {
    if (selectedAuditors?.length === 0) {
      showErrorToast(toast, "Please select at least one auditor.");
      return;
    }

    // Disabled since we are setting from backend automatically.
    // if (!endDate?.day || !endDate?.month || !endDate?.year) {
    //   showErrorToast(toast, "Please add end date.");
    //   return;
    // }

    const dataToSubmit = {
      stageId: stageId,
      type: auditType,
      lockAnswers: lockAnswers === "Yes",
      autoClose: autoClose === "Yes",
      // startDate: formatDateForAPI(startDate),
      // endDate: formatDateForAPI(endDate),
      auditors: selectedAuditors.map((auditor) => auditor.value),
    };
    saveAudit(dataToSubmit);
  };

  const typeOptions = [
    { value: "ROUND_SUMMARY", label: "Round Summary" },
    { value: "QUESTION", label: "Question" },
  ];

  const { data: activeAuditData, isGetActiveLoading } = useGetActiveAudit(stageId, {
    enabled: Boolean(stageId),
    cacheTime: 0,
    refetchOnMount: true,
    staleTime: 0,
  });

  if (isGetActiveLoading) {
    return <LocalSpinner />;
  }

  if (activeAuditData) {
    return (
      <IssbContainer margin="20%">
        <Text fontSize="heading.m">{stageName} has already started</Text>
        <Button
          mt={4}
          onClick={() => navigate(`${routes.issbCloseAudit}?stageId=${stageId}&stageName=${stageName}`)}
          variant="secondary"
        >
          Complete the {stageName}
        </Button>
      </IssbContainer>
    );
  }

  return (
    <IssbContainer margin="20%">
      <VStack align="start" spacing={8}>
        <Text fontSize="heading.2xl" fontWeight="bold" color="darkTeal">
          Start {stageName}
        </Text>
        <Text fontSize="body.m" color="gray.500">
          From here you can initiate an audit and manage its settings.
        </Text>
      </VStack>

      <Box>
        <Text fontSize="body.s" fontWeight="500" color="gray.500" mb={2}>
          Audit Type
        </Text>
        <RadioGroup value={auditType} onChange={setAuditType} colorScheme="limeShade">
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
            <Box
              p={6}
              bg={auditType === "ROUND_SUMMARY" ? "lightLime" : "pureWhite"}
              borderRadius="md"
              border="1px"
              borderColor={auditType === "ROUND_SUMMARY" ? "limeShade.500" : "gray.200"}
            >
              <Radio value="ROUND_SUMMARY" isDisabled={true}>
                Round Summary
              </Radio>
            </Box>
            <Box
              p={6}
              bg={auditType === "QUESTION" ? "lightLime" : "pureWhite"}
              borderRadius="md"
              border="1px"
              borderColor={auditType === "QUESTION" ? "limeShade.500" : "gray.200"}
            >
              <Radio value="QUESTION">Question</Radio>
            </Box>
          </SimpleGrid>
        </RadioGroup>
      </Box>

      <Divider my={2} />

      {/* Disabled since we only doing question level and not needed now */}
      {/*<Flex gap={4} wrap="wrap">*/}
      {/*  <Box flex="1" minW="250px">*/}
      {/*    <Text fontSize="body.s" fontWeight="500" color="gray.600" mb={2}>*/}
      {/*      Select status of question to include*/}
      {/*    </Text>*/}
      {/*    <Select placeholder="Choose status from dropdown">*/}
      {/*      <option value="status1">Ready for Audit</option>*/}
      {/*      /!*<option value="status2">Status 2</option>*!/*/}
      {/*    </Select>*/}
      {/*  </Box>*/}

      {/*  <Box flex="1" minW="250px">*/}
      {/*    <Text fontSize="body.s" fontWeight="500" color="gray.600" mb={2}>*/}
      {/*      Select question groups to include*/}
      {/*    </Text>*/}
      {/*    <Select placeholder="Choose group from dropdown">*/}
      {/*      <option value="group1">Remuneration</option>*/}
      {/*      <option value="group2">Carbon Pricing</option>*/}
      {/*    </Select>*/}
      {/*  </Box>*/}
      {/*</Flex>*/}

      {/* Disabled since we only doing question level and not needed now */}
      {/*<Box>*/}
      {/*  <Text fontSize="body.s" fontWeight="500" color="gray.600" mb={2}>*/}
      {/*    Lock all answers while audit running?*/}
      {/*  </Text>*/}
      {/*  <RadioGroup value={lockAnswers} onChange={setLockAnswers} colorScheme="limeShade">*/}
      {/*    <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>*/}
      {/*      <Box*/}
      {/*        key="Yes"*/}
      {/*        p={6}*/}
      {/*        bg={lockAnswers === "Yes" ? "lightLime" : "pureWhite"}*/}
      {/*        borderRadius="md"*/}
      {/*        border="1px"*/}
      {/*        borderColor={lockAnswers === "Yes" ? "limeShade.500" : "gray.200"}*/}
      {/*      >*/}
      {/*        <Radio value="Yes">Yes</Radio>*/}
      {/*      </Box>*/}
      {/*      <Box*/}
      {/*        key="No"*/}
      {/*        p={6}*/}
      {/*        bg={lockAnswers === "No" ? "lightLime" : "pureWhite"}*/}
      {/*        borderRadius="md"*/}
      {/*        border="1px"*/}
      {/*        borderColor={lockAnswers === "No" ? "limeShade.500" : "gray.200"}*/}
      {/*      >*/}
      {/*        <Radio value="No">No</Radio>*/}
      {/*      </Box>*/}
      {/*    </SimpleGrid>*/}
      {/*  </RadioGroup>*/}
      {/*</Box>*/}

      <Flex gap={4} wrap="wrap">
        <Box flex="1" minW="250px">
          <Text fontSize="body.s" fontWeight="500" color="gray.600" mb={2}>
            Select auditors
          </Text>
          <MultiSelect
            options={userList}
            size="lg"
            value={selectedAuditors}
            onChange={(selected) => setSelectedAuditors(selected)}
            placeholder="Choose from dropdown"
          />
        </Box>

        {/*<Box flex="1" minW="250px">*/}
        {/*<Text fontSize="body.s" fontWeight="500" color="gray.600" mb={2}>*/}
        {/*  Select entities*/}
        {/*</Text>*/}
        {/*<MultiSelect*/}
        {/*  options={entitiesList}*/}
        {/*  size="lg"*/}
        {/*  value={selectedEntities}*/}
        {/*  onChange={(selected) => setSelectedEntities(selected)}*/}
        {/*  placeholder="Choose from dropdown"*/}
        {/*/>*/}
        {/*</Box>*/}
      </Flex>

      {/*<SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>*/}
      {/*  <Box>*/}
      {/*    <Text fontSize="body.s" fontWeight="500" color="gray.600" mb={2}>*/}
      {/*      Start date*/}
      {/*    </Text>*/}
      {/*    <HStack>*/}
      {/*      <Input*/}
      {/*        placeholder="DD"*/}
      {/*        value={startDate.day}*/}
      {/*        onChange={(e) => handleDateChange("start", "day", e.target.value)}*/}
      {/*      />*/}
      {/*      <Input*/}
      {/*        placeholder="MM"*/}
      {/*        value={startDate.month}*/}
      {/*        onChange={(e) => handleDateChange("start", "month", e.target.value)}*/}
      {/*      />*/}
      {/*      <Input*/}
      {/*        placeholder="YYYY"*/}
      {/*        value={startDate.year}*/}
      {/*        onChange={(e) => handleDateChange("start", "year", e.target.value)}*/}
      {/*      />*/}
      {/*    </HStack>*/}
      {/*  </Box>*/}

      {/*  <Box>*/}
      {/*    <Text fontSize="body.s" fontWeight="500" color="gray.600" mb={2}>*/}
      {/*      Target End Date*/}
      {/*    </Text>*/}
      {/*    <HStack>*/}
      {/*      <Input*/}
      {/*        placeholder="DD"*/}
      {/*        value={endDate.day}*/}
      {/*        onChange={(e) => handleDateChange("end", "day", e.target.value)}*/}
      {/*      />*/}
      {/*      <Input*/}
      {/*        placeholder="MM"*/}
      {/*        value={endDate.month}*/}
      {/*        onChange={(e) => handleDateChange("end", "month", e.target.value)}*/}
      {/*      />*/}
      {/*      <Input*/}
      {/*        placeholder="YYYY"*/}
      {/*        value={endDate.year}*/}
      {/*        onChange={(e) => handleDateChange("end", "year", e.target.value)}*/}
      {/*      />*/}
      {/*    </HStack>*/}
      {/*  </Box>*/}
      {/*</SimpleGrid>*/}

      {/*<Box>*/}
      {/*  <Text fontSize="body.s" fontWeight="500" color="gray.600" mb={2}>*/}
      {/*    Automatically close audit on end date?*/}
      {/*  </Text>*/}
      {/*  <RadioGroup value={autoClose} onChange={setAutoClose} colorScheme="limeShade">*/}
      {/*    <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>*/}
      {/*      <Box*/}
      {/*        key="Yes"*/}
      {/*        p={6}*/}
      {/*        bg={autoClose === "Yes" ? "lightLime" : "pureWhite"}*/}
      {/*        borderRadius="md"*/}
      {/*        border="1px"*/}
      {/*        borderColor={autoClose === "Yes" ? "limeShade.500" : "gray.200"}*/}
      {/*      >*/}
      {/*        <Radio value="Yes">Yes</Radio>*/}
      {/*      </Box>*/}
      {/*      <Box*/}
      {/*        key="No"*/}
      {/*        p={6}*/}
      {/*        bg={autoClose === "No" ? "lightLime" : "pureWhite"}*/}
      {/*        borderRadius="md"*/}
      {/*        border="1px"*/}
      {/*        borderColor={autoClose === "No" ? "limeShade.500" : "gray.200"}*/}
      {/*      >*/}
      {/*        <Radio value="No">No</Radio>*/}
      {/*      </Box>*/}
      {/*    </SimpleGrid>*/}
      {/*  </RadioGroup>*/}
      {/*</Box>*/}

      <Flex justify="space-between">
        <Button variant="outline" colorScheme="gray" onClick={() => navigate(`${routes.issbRequirements}?rtab=3`)} w="120px">
          Exit
        </Button>
        <Button variant="primary" onClick={() => handleStartAudit()}>
          Start Audit
        </Button>
      </Flex>
    </IssbContainer>
  );
};

export default IssbStartAudit;
